import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { BrowserRouter as Router } from 'react-router-dom';
import { ThemeContext } from '../contexts/ThemeContext';
import type { ThemeContextType, ThemeMode } from '../contexts/ThemeContext';

const THEME_KEY = 'dev13.theme';

function prefersDark(): boolean {
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function readMode(): ThemeMode {
  const saved = localStorage.getItem(THEME_KEY);
  if (saved === 'light' || saved === 'dark' || saved === 'system') {
    return saved;
  }
  return 'system'; // no choice yet → follow the OS, as before
}

function resolveDark(mode: ThemeMode): boolean {
  switch (mode) {
    case 'light':
      return false;
    case 'dark':
      return true;
    default:
      return prefersDark();
  }
}

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mode, setModeState] = useState<ThemeMode>(readMode);
  const [isDarkMode, setIsDarkMode] = useState(() => resolveDark(readMode()));

  // Resolve the appearance for the current mode; `system` stays live with the OS.
  useEffect(() => {
    const apply = () => setIsDarkMode(resolveDark(mode));
    apply();

    if (mode === 'system') {
      const mq = window.matchMedia('(prefers-color-scheme: dark)');
      mq.addEventListener('change', apply);
      return () => mq.removeEventListener('change', apply);
    }
    return undefined;
  }, [mode]);

  // Apply the resolved theme to <html>.
  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDarkMode);
  }, [isDarkMode]);

  const setMode = useCallback((next: ThemeMode) => {
    localStorage.setItem(THEME_KEY, next);
    setModeState(next);
  }, []);

  const toggleTheme = useCallback(() => {
    // Simple icon button: flip to an explicit light/dark from whatever shows now.
    setMode(isDarkMode ? 'light' : 'dark');
  }, [isDarkMode, setMode]);

  const contextValue = useMemo<ThemeContextType>(
    () => ({ isDarkMode, mode, setMode, toggleTheme }),
    [isDarkMode, mode, setMode, toggleTheme],
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      <Router>{children}</Router>
    </ThemeContext.Provider>
  );
};